import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function LinkPollModal({ post, onClose, onSaved }) {
  const [polls, setPolls] = useState([]);
  const [pollId, setPollId] = useState("");
  const [text, setText] = useState("Take the poll");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/posts", { params: { type: "poll", status: "approved" } })
      .then((r) => {
        const list = (r.data || []).filter((p) => p.id !== post.id);
        setPolls(list);
        if (list.length > 0) setPollId(list[0].id);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [post.id]);

  const save = async (e) => {
    e.preventDefault();
    const linkText = text.trim();
    if (!linkText) {
      toast.error("Please enter the text for the link.");
      return;
    }
    if (!pollId) {
      toast.error("Please pick a poll to link to.");
      return;
    }
    const snippet = `[${linkText}](/post/${pollId})`;
    const existing = (post.body || "").trimEnd();
    const newBody = existing ? `${existing}\n\n${snippet}` : snippet;
    setSaving(true);
    try {
      await api.put(`/admin/posts/${post.id}`, { body: newBody });
      toast.success("Poll link added to the piece.");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-black w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="link-poll-modal"
      >
        <div className="flex items-center justify-between border-b border-black px-6 py-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-700">Editorial link</div>
            <h3 className="serif text-2xl mt-1">Link a poll</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="link-poll-close"
            className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black"
          >
            Close
          </button>
        </div>

        <form onSubmit={save} className="p-6 space-y-6">
          <p className="text-sm text-neutral-700">
            Append a clickable link to the end of &ldquo;{post.title}&rdquo; that sends readers to a poll on the site.
          </p>

          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Link text</label>
            <input
              data-testid="link-poll-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={120}
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none"
              placeholder="Take the poll"
            />
          </div>

          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Poll</label>
            {loading ? (
              <p className="mt-2 mono text-xs text-neutral-500">Loading polls&hellip;</p>
            ) : polls.length === 0 ? (
              <p className="mt-2 mono text-xs text-neutral-500">No approved polls to link to yet.</p>
            ) : (
              <select
                data-testid="link-poll-select"
                value={pollId}
                onChange={(e) => setPollId(e.target.value)}
                className="mt-2 w-full border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none serif text-lg"
              >
                {polls.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="border border-dashed border-neutral-300 p-3 mono text-xs text-neutral-600">
            <span className="uppercase tracking-[0.25em] text-neutral-400 block mb-1">Preview snippet</span>
            <code>{`[${(text || "…").trim() || "…"}](/post/${pollId || "POLL_ID"})`}</code>
          </div>

          <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="link-poll-save"
              disabled={saving || loading || polls.length === 0}
              className="bg-black text-white px-6 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 disabled:opacity-40"
            >
              {saving ? "Adding…" : "Add link"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
